import React, { useState, useEffect, useRef } from 'react';
import axios from '../lib/axios';
import { useModules } from '../lib/useModules';
import {
  Bot,
  Settings,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Timer,
  CheckCircle2,
  BrainCircuit,
  Zap,
  Loader2,
  Key,
  Globe,
  Cpu,
  ChevronDown,
  Maximize2,
  Sparkles
} from 'lucide-react';
import AiUsageChart from '../components/settings/AiUsageChart';
import PromptEditorModal from '../components/settings/PromptEditorModal';
import HorarioSemanal from '../components/settings/HorarioSemanal';
import TrasEscalar from '../components/settings/TrasEscalar';

// La lista de modelos ya no vive acá. Estaba escrita a mano y envejecía sin
// avisar: el 19/09/2026 ofrecía cinco modelos que ya no existían y no dejaba
// elegir los nuevos. Ahora la pide el backend al propio proveedor con la clave
// del tenant (ver CatalogoDeModelos), y sólo cae a una lista de referencia si
// todavía no hay clave.
const PROVIDER_LABELS = {
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  deepseek: 'DeepSeek',
  anthropic: 'Anthropic',
  kimi: 'Kimi (Moonshot)',
  openrouter: 'OpenRouter'
};

// Con listas largas (OpenRouter trae cientos) un <select> es inusable: se
// cambia por un campo con autocompletado.
const LIMITE_SELECT = 40;

const TIMEZONES = [
  { group: 'Venezuela', options: [{ value: 'America/Caracas', label: 'Caracas (UTC-4)' }] },
  { group: 'Colombia / Perú / Ecuador', options: [
    { value: 'America/Bogota', label: 'Bogotá (UTC-5)' },
    { value: 'America/Lima', label: 'Lima (UTC-5)' },
    { value: 'America/Guayaquil', label: 'Guayaquil (UTC-5)' },
  ]},
  { group: 'México', options: [
    { value: 'America/Mexico_City', label: 'Ciudad de México (UTC-6)' },
    { value: 'America/Cancun', label: 'Cancún (UTC-5)' },
    { value: 'America/Tijuana', label: 'Tijuana (UTC-7)' },
  ]},
  { group: 'Cono Sur', options: [
    { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (UTC-3)' },
    { value: 'America/Santiago', label: 'Santiago (UTC-3/-4)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
    { value: 'America/Montevideo', label: 'Montevideo (UTC-3)' },
  ]},
  { group: 'Caribe y Centroamérica', options: [
    { value: 'America/Santo_Domingo', label: 'Santo Domingo (UTC-4)' },
    { value: 'America/Puerto_Rico', label: 'Puerto Rico (UTC-4)' },
    { value: 'America/Panama', label: 'Panamá (UTC-5)' },
    { value: 'America/Costa_Rica', label: 'Costa Rica (UTC-6)' },
  ]},
  { group: 'Estados Unidos', options: [
    { value: 'America/New_York', label: 'Nueva York (UTC-5/-4)' },
    { value: 'America/Chicago', label: 'Chicago (UTC-6/-5)' },
    { value: 'America/Denver', label: 'Denver (UTC-7/-6)' },
    { value: 'America/Los_Angeles', label: 'Los Ángeles (UTC-8/-7)' },
  ]},
  { group: 'Europa', options: [
    { value: 'Europe/Madrid', label: 'Madrid (UTC+1/+2)' },
    { value: 'Europe/London', label: 'Londres (UTC+0/+1)' },
  ]},
  { group: 'Universal', options: [{ value: 'UTC', label: 'UTC (Universal)' }] },
];

const AiStrategySettings = () => {
  const { isFeatureEnabled } = useModules();
  // Sin la función de Tasas BCV activa, los placeholders {tasa_bcv} no se
  // reemplazan por nada al armar el prompt, así que sugerirlos sólo confunde.
  const bcvEnabled = isFeatureEnabled('settings', 'bcv');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [strategy, setStrategy] = useState({
    provider: 'openai',
    api_key: '',
    api_url: '',
    model_name: 'gpt-4o-mini',
    master_prompt: '',
    media_prompt: '',
    ai_mode: 'off',
    qualification_threshold: 70,
    reassignment_timeout_minutes: 15,
    tras_escalar: 'callar',
    max_reasignaciones: 2,
    espera_agrupacion_segundos: 30,
    inventory_tool_url: '',
    inventory_tool_description: '',
    inventory_trigger_keywords: '',
    inventory_wait_message: '',
    is_active: true,
    archive_threshold_days: 7,
    is_tutor_enabled: false,
    timezone: 'America/Caracas'
  });

  const [models, setModels] = useState([]);
  const [modelsSource, setModelsSource] = useState(null);
  const [recommendedModel, setRecommendedModel] = useState(null);
  const [modelsError, setModelsError] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);
  // Hasta que llega la primera lista no hay contra qué comparar el modelo
  // guardado: sin esta marca, el primer render veía la lista vacía y mostraba
  // un instante el campo de "modelo personalizado".
  const [listaCargada, setListaCargada] = useState(false);
  // Al cambiar de proveedor el modelo anterior deja de tener sentido: se
  // preselecciona el que el backend marca como recomendado.
  const elegirPrimeroAlCargar = useRef(false);

  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeModalPrompt, setActiveModalPrompt] = useState({
    isOpen: false,
    type: 'master',
    title: '',
    value: ''
  });

  const openEditorModal = (type) => {
    setActiveModalPrompt({
      isOpen: true,
      type,
      title: type === 'master' ? 'Instrucciones del Agente (Master Prompt)' : 'Instrucciones para Imágenes y Audios (Media Prompt)',
      value: type === 'master' ? (strategy.master_prompt || '') : (strategy.media_prompt || '')
    });
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/leads/settings');
      const data = response.data;
      setStrategy(data);
      cargarModelos(data.provider, data.api_key, data.api_url);
    } catch (error) {
      console.error('Error fetching AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pide al backend los modelos del proveedor. La clave va aunque no esté
  // guardada todavía: se consulta justamente para ver qué se puede elegir
  // antes de guardar.
  const cargarModelos = async (provider, apiKey, apiUrl) => {
    setLoadingModels(true);
    setModelsError(null);
    try {
      const { data } = await axios.post('/api/leads/settings/models', {
        provider,
        api_key: apiKey || null,
        api_url: apiUrl || null
      });
      setModels(data.models || []);
      setModelsSource(data.source);
      setRecommendedModel(data.recommended || null);
      setModelsError(data.error || null);
    } catch (error) {
      setModels([]);
      setModelsSource(null);
      setModelsError('No se pudo cargar la lista de modelos. Puedes escribir el ID a mano.');
    } finally {
      setLoadingModels(false);
      setListaCargada(true);
    }
  };

  // Cuando llega la lista se decide si el modelo guardado está en ella o es
  // uno escrito a mano. Antes esto se calculaba contra la lista fija y los
  // tenants con un modelo nuevo lo veían como "personalizado".
  useEffect(() => {
    if (loadingModels || !listaCargada) return;

    if (elegirPrimeroAlCargar.current && models.length > 0) {
      elegirPrimeroAlCargar.current = false;
      // El recomendado lo decide el backend. "El primero" no sirve: OpenRouter
      // ordena por fecha y el primero es el último modelo que alguien subió.
      setStrategy(s => ({ ...s, model_name: recommendedModel || models[0].value }));
      setIsCustomMode(false);
      return;
    }

    if (models.length > LIMITE_SELECT) {
      setIsCustomMode(false); // el campo con autocompletado ya admite cualquier ID
      return;
    }
    setIsCustomMode(!!strategy.model_name && !models.some(m => m.value === strategy.model_name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models, loadingModels, listaCargada]);

  const handleProviderChange = (newProvider) => {
    elegirPrimeroAlCargar.current = true;
    setStrategy({ ...strategy, provider: newProvider, model_name: '' });
    setIsCustomMode(false);
    // La clave cargada es del proveedor anterior: no sirve para este. El
    // backend usa la guardada sólo si coincide el proveedor.
    cargarModelos(newProvider, null, null);
  };

  const handleModelChange = (val) => {
    if (val === 'custom_model_input') {
      setIsCustomMode(true);
      setStrategy({ ...strategy, model_name: '' });
    } else {
      setIsCustomMode(false);
      setStrategy({ ...strategy, model_name: val });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.post('/api/leads/settings', strategy);
      setMessage({ type: 'success', text: 'Configuración guardada correctamente' });
      fetchSettings();
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al guardar la configuración' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="animate-spin text-primary" size={40} />
        <p className="text-slate-500 dark:text-gray-400 animate-pulse font-medium">Sincronizando modelos de IA...</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-8 animate-in fade-in duration-700">

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 blur-[100px] -mr-32 -mt-32 rounded-full"></div>
        <div className="relative z-10 flex items-center gap-5">
          <div className="p-4 bg-primary rounded-2xl shadow-lg shadow-primary/20">
            <BrainCircuit className="text-white" size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              Estrategia de IA
              <span className="text-[10px] bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-1 rounded-full border border-green-500/20 uppercase tracking-widest leading-none font-bold">Autopilot Ready</span>
            </h1>
            <p className="text-slate-500 dark:text-gray-400 mt-1 font-medium">Configura el cerebro y comportamiento del agente virtual</p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className={`relative z-10 px-8 py-4 rounded-2xl flex items-center justify-center gap-3 font-black tracking-widest uppercase text-sm transition-all shadow-lg ${saving
              ? 'bg-slate-100 text-slate-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed'
              : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:scale-105 active:scale-95'
            }`}
        >
          {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={18} />}
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Left Column (Main Form) */}
        <div className="lg:col-span-8 space-y-8">

          {/* API Configuration Card */}
          <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-blue-500/10 rounded-lg text-blue-600 dark:text-blue-400">
                <ShieldCheck size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Proveedor e Infraestructura</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Cpu size={12} /> Motor de IA
                </label>
                <div className="relative">
                  <select
                    value={strategy.provider}
                    onChange={(e) => handleProviderChange(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-bold appearance-none cursor-pointer"
                  >
                    {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Key size={12} /> API Key
                </label>
                <input
                  type="password"
                  placeholder="Ingrese su API Key"
                  value={strategy.api_key}
                  autoComplete="new-password"
                  onChange={(e) => setStrategy({ ...strategy, api_key: e.target.value })}
                  // Al terminar de pegar la clave se piden los modelos que esa
                  // cuenta puede usar.
                  onBlur={() => cargarModelos(strategy.provider, strategy.api_key, strategy.api_url)}
                  className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-slate-300 dark:placeholder:text-gray-600 font-mono transition-all"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Bot size={12} /> Modelo ID
                  </label>
                  <button
                    type="button"
                    onClick={() => cargarModelos(strategy.provider, strategy.api_key, strategy.api_url)}
                    disabled={loadingModels}
                    className="text-[11px] font-bold text-primary hover:underline disabled:opacity-50 disabled:no-underline flex items-center gap-1"
                  >
                    {loadingModels ? <Loader2 size={11} className="animate-spin" /> : null}
                    {loadingModels ? 'Consultando…' : 'Actualizar lista'}
                  </button>
                </div>

                {models.length > LIMITE_SELECT ? (
                  // Cientos de modelos (OpenRouter): se escribe para filtrar.
                  <>
                    <input
                      type="text"
                      list="lista-modelos"
                      placeholder="Escribe para buscar, ej. deepseek o claude"
                      value={strategy.model_name}
                      onChange={(e) => setStrategy({ ...strategy, model_name: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-mono"
                    />
                    <datalist id="lista-modelos">
                      {models.map(model => (
                        <option key={model.value} value={model.value}>{model.label}</option>
                      ))}
                    </datalist>
                  </>
                ) : (
                  <div className="relative">
                    <select
                      value={isCustomMode ? 'custom_model_input' : strategy.model_name}
                      onChange={(e) => handleModelChange(e.target.value)}
                      disabled={loadingModels && models.length === 0}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-bold appearance-none cursor-pointer disabled:opacity-60"
                    >
                      {loadingModels && models.length === 0 && <option value="">Cargando modelos…</option>}
                      {models.map(model => (
                        <option key={model.value} value={model.value}>{model.label}</option>
                      ))}
                      <option value="custom_model_input">Otro (Especificar ID...)</option>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                  </div>
                )}

                {!loadingModels && modelsSource === 'proveedor' && (
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-400">
                    {strategy.provider === 'openrouter'
                      ? `Catálogo en vivo de OpenRouter · ${models.length} modelos · escribe para buscar`
                      : `Lista traída de ${PROVIDER_LABELS[strategy.provider]} con tu clave · ${models.length} modelos disponibles`}
                  </p>
                )}
                {!loadingModels && modelsSource === 'referencia' && !modelsError && (
                  <p className="text-[11px] text-slate-500 dark:text-gray-400">
                    Lista de referencia. Ingresa tu clave para ver exactamente los modelos que tu cuenta puede usar.
                  </p>
                )}
                {!loadingModels && modelsError && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">{modelsError}</p>
                )}

                {isCustomMode && models.length <= LIMITE_SELECT && (
                  <input
                    type="text"
                    placeholder="Escriba el ID del modelo personalizado"
                    value={strategy.model_name}
                    onChange={(e) => setStrategy({ ...strategy, model_name: e.target.value })}
                    className="w-full mt-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-mono"
                  />
                )}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <Globe size={12} /> API URL (Opcional)
                </label>
                <input
                  type="text"
                  placeholder="https://api.custom.com"
                  value={strategy.api_url || ''}
                  onChange={(e) => setStrategy({ ...strategy, api_url: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                />
              </div>
              <div className="space-y-4 md:col-span-2 border-t border-slate-200 dark:border-white/10 pt-6 mt-4">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Zap size={16} className="text-yellow-500" /> Herramienta Externa de Datos (Webhook)
                </h3>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                    URL del Webhook (ej. n8n)
                  </label>
                  <input
                    type="text"
                    placeholder="https://tu-n8n.com/webhook/products"
                    value={strategy.inventory_tool_url || ''}
                    onChange={(e) => setStrategy({ ...strategy, inventory_tool_url: e.target.value })}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-medium text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                    Descripción para la IA
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Buscar precios y stock de neumáticos"
                    value={strategy.inventory_tool_description || ''}
                    onChange={(e) => setStrategy({ ...strategy, inventory_tool_description: e.target.value })}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                      Palabras Clave (Separadas por coma)
                    </label>
                    <input
                      type="text"
                      placeholder="precio, stock, disponibilidad, rin"
                      value={strategy.inventory_trigger_keywords || ''}
                      onChange={(e) => setStrategy({ ...strategy, inventory_trigger_keywords: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex justify-between">
                      <span>Mensaje de Espera (Cliente)</span>
                      <span className="text-[10px] text-slate-400 dark:text-gray-500 lowercase normal-case">Vacío para desactivar</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Ej: Consultando precios, un momento... (Dejar vacío para desactivar)"
                      value={strategy.inventory_wait_message || ''}
                      onChange={(e) => setStrategy({ ...strategy, inventory_wait_message: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm"
                    />
                  </div>
                </div>

                <p className="text-[10px] text-slate-400 font-bold uppercase px-1 mt-2">
                  La herramienta se activará automáticamente al detectar las palabras clave.
                </p>
              </div>
            </div>
          </div>

          {/* Master Prompt Card */}
          <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg relative overflow-hidden">
            <div className="flex items-center justify-between gap-3 mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg text-purple-600 dark:text-purple-400">
                  <BrainCircuit size={20} />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Instrucciones del Agente (Master Prompt)</h2>
              </div>
              <button
                type="button"
                onClick={() => openEditorModal('master')}
                className="px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-700 dark:text-purple-300 rounded-xl text-xs font-extrabold flex items-center gap-2 transition-all border border-purple-500/20 shadow-sm active:scale-95"
              >
                <Maximize2 size={14} />
                <span>Pantalla Completa</span>
              </button>
            </div>
            <textarea
              rows="10"
              value={strategy.master_prompt || ''}
              onChange={(e) => setStrategy({ ...strategy, master_prompt: e.target.value })}
              placeholder="Escribe aquí las directrices para la IA..."
              className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-2xl px-6 py-6 text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none font-sans text-base leading-relaxed"
            ></textarea>
            <div className="flex flex-col gap-2 mt-4 text-[11px] text-slate-500 dark:text-gray-500 font-bold uppercase tracking-wider px-2">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-yellow-500 shrink-0" />
                <span>Consejo: Define un rol específico (ej. "Eres un asistente de soporte bilingüe")</span>
              </div>
              {bcvEnabled && (
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <Zap size={14} className="text-emerald-500 shrink-0" />
                <span>Tasas BCV: Usa <code className="bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 rounded text-[10px] font-mono text-emerald-700 dark:text-emerald-400">{"{tasa_bcv}"}</code> (Dólar) o <code className="bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 rounded text-[10px] font-mono text-emerald-700 dark:text-emerald-400">{"{tasa_bcv_eur}"}</code> (Euro) para las tasas cambiarias.</span>
              </div>
              )}
              <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                <Zap size={14} className="text-indigo-500 shrink-0" />
                <span>Fecha/Hora: Usa <code className="bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 rounded text-[10px] font-mono text-indigo-700 dark:text-indigo-400">{"{fecha_actual}"}</code>, <code className="bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 rounded text-[10px] font-mono text-indigo-700 dark:text-indigo-400">{"{hora_actual}"}</code> o <code className="bg-slate-100 dark:bg-white/5 px-1.5 py-0.5 rounded text-[10px] font-mono text-indigo-700 dark:text-indigo-400">{"{dia_semana}"}</code> para el tiempo actual.</span>
              </div>
            </div>
          </div>

          {/* Media Prompt Card */}
          <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg relative overflow-hidden">
            <div className="flex items-center justify-between gap-3 mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-500/10 rounded-lg text-pink-600 dark:text-pink-400">
                  <BrainCircuit size={20} />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Instrucciones para Imágenes y Audios (Media Prompt)</h2>
              </div>
              <button
                type="button"
                onClick={() => openEditorModal('media')}
                className="px-4 py-2 bg-pink-500/10 hover:bg-pink-500/20 text-pink-700 dark:text-pink-300 rounded-xl text-xs font-extrabold flex items-center gap-2 transition-all border border-pink-500/20 shadow-sm active:scale-95"
              >
                <Maximize2 size={14} />
                <span>Pantalla Completa</span>
              </button>
            </div>
            <textarea
              rows="6"
              value={strategy.media_prompt || ''}
              onChange={(e) => setStrategy({ ...strategy, media_prompt: e.target.value })}
              placeholder="Escribe aquí cómo debe interpretar la IA las imágenes y audios. Ej: si no se entiende, responde [ESCALAR_A_HUMANO]..."
              className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-2xl px-6 py-6 text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none font-sans text-base leading-relaxed"
            ></textarea>
            <div className="flex items-center gap-2 mt-4 text-[11px] text-slate-500 dark:text-gray-500 font-bold uppercase tracking-wider px-2">
              <Zap size={14} className="text-yellow-500" />
              Consejo: Define directivas claras como: "Si no entiendes el audio, responde únicamente [ESCALAR_A_HUMANO]"
            </div>
          </div>
        </div>

        {/* Right Column (Logic & States) */}
        <div className="lg:col-span-4 space-y-8">

          {/* Operation Mode Card */}
          <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-yellow-500/10 rounded-lg text-yellow-600">
                <Zap size={20} fill="currentColor" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Modo de Operación</h2>
            </div>

            <div className="space-y-4">
              {[
                { id: 'off', label: 'Desactivado', desc: 'Control humano total.' },
                { id: 'light', label: 'Modo Asistido', desc: 'Calificación y sugerencias.' },
                { id: 'autopilot', label: 'Piloto Automático', desc: 'Respuesta proactiva.' },
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setStrategy({ ...strategy, ai_mode: mode.id })}
                  className={`w-full text-left p-6 rounded-2xl border transition-all relative overflow-hidden group ${strategy.ai_mode === mode.id
                      ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20'
                      : 'bg-slate-50 dark:bg-white/5 border-slate-100 dark:border-white/5 text-slate-600 dark:text-gray-400 hover:border-slate-300 dark:hover:border-white/20'
                    }`}
                >
                  <div className="font-black flex items-center justify-between relative z-10 text-sm uppercase tracking-widest">
                    {mode.label}
                    {strategy.ai_mode === mode.id && <CheckCircle2 size={18} className="text-white" />}
                  </div>
                  <div className={`text-xs mt-2 relative z-10 leading-relaxed font-medium ${strategy.ai_mode === mode.id ? 'opacity-90' : 'opacity-60'}`}>
                    {mode.desc}
                  </div>
                </button>
              ))}
            </div>

            {/* Horario del piloto automatico: fuera de la franja baja a modo
                asistido sin tocar el modo configurado. El calculo vive en el
                backend (AiStrategy::effectiveAiMode); esto solo lo configura. */}
            {strategy.ai_mode === 'autopilot' && (
              <div className="mt-6 p-5 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-slate-800 dark:text-white">Horario de trabajo</div>
                    <p className="text-xs text-slate-500 dark:text-gray-400 mt-1 leading-relaxed">
                      Fuera de este horario la IA pasa a <b>Modo Asistido</b>: califica y sugiere, pero no responde sola.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={strategy.autopilot_schedule_enabled || false}
                      onChange={(e) => setStrategy({ ...strategy, autopilot_schedule_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 dark:bg-white/10 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                  </label>
                </div>

                {strategy.autopilot_schedule_enabled && (
                  <HorarioSemanal
                    value={strategy.autopilot_schedule_days}
                    onChange={(dias) => setStrategy({ ...strategy, autopilot_schedule_days: dias })}
                    timezone={strategy.timezone || 'America/Caracas'}
                  />
                )}
              </div>
            )}
          </div>

          {/* Tutor AI Moderation Card */}
          <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg text-purple-600 dark:text-purple-400">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Módulo Tutor AI</h2>
                  <p className="text-xs text-slate-500 dark:text-gray-400 font-medium">Aprobación previa de respuestas en Piloto Automático</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={strategy.is_tutor_enabled || false}
                  onChange={(e) => setStrategy({ ...strategy, is_tutor_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-white/10 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            <div className={`p-4 rounded-xl text-xs font-medium border ${strategy.is_tutor_enabled ? 'bg-purple-500/10 border-purple-500/20 text-purple-700 dark:text-purple-300' : 'bg-slate-50 dark:bg-white/5 border-slate-100 dark:border-white/5 text-slate-500 dark:text-gray-400'}`}>
              {strategy.is_tutor_enabled ? (
                <span><strong>Moderación Activada:</strong> Las respuestas generadas en Piloto Automático se pausarán y requerirán la aprobación de un supervisor en el módulo <a href="/tutor" className="underline font-bold text-purple-600 dark:text-purple-400">Tutor AI</a> antes de enviarse al cliente.</span>
              ) : (
                <span><strong>Moderación Desactivada:</strong> Las respuestas del Piloto Automático se enviarán directamente al cliente sin pasar por aprobación previa.</span>
              )}
            </div>
          </div>

          {/* Business Rules Card */}
          <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-indigo-500/10 rounded-lg text-indigo-600 dark:text-indigo-400">
                <SlidersHorizontal size={20} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Lógica de Negocio</h2>
            </div>

            <div className="space-y-10">
              <div className="space-y-4">
                <div className="flex justify-between items-end">
                  <label className="text-xs font-black text-slate-500 dark:text-gray-400 uppercase tracking-widest">Umbral de Calificación</label>
                  <span className="text-2xl font-black text-slate-900 dark:text-white">{strategy.qualification_threshold}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={strategy.qualification_threshold}
                  onChange={(e) => setStrategy({ ...strategy, qualification_threshold: parseInt(e.target.value) })}
                  className="w-full h-2 bg-slate-100 dark:bg-white/10 rounded-full appearance-none cursor-pointer accent-primary"
                />
                <p className="text-[10px] text-slate-400 font-bold uppercase">Puntaje mínimo para escalar un Lead</p>
              </div>

              <TrasEscalar value={strategy} onChange={setStrategy} />

              <div className="space-y-4">
                <label className="text-xs font-black text-slate-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <Timer size={14} /> Espera antes de responder
                </label>
                <div className="flex items-center gap-4 bg-slate-50 dark:bg-white/5 p-4 rounded-2xl border border-slate-100 dark:border-white/5">
                  <input
                    type="number"
                    min="5"
                    max="120"
                    value={strategy.espera_agrupacion_segundos ?? 30}
                    onChange={(e) => setStrategy({ ...strategy, espera_agrupacion_segundos: parseInt(e.target.value) })}
                    className="w-20 bg-transparent text-3xl font-black text-slate-900 dark:text-white focus:outline-none border-r border-slate-200 dark:border-white/10"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-900 dark:text-white">Segundos</p>
                    <p className="text-[10px] text-slate-400">Se cuentan desde el <strong>último</strong> mensaje del cliente. Si sigue escribiendo, la espera se reinicia y la IA responde una sola vez con todo el contexto.</p>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 font-bold uppercase">Entre 5 y 120 · Recomendado 30</p>
              </div>

              <div className="space-y-4">
                <label className="text-xs font-black text-slate-500 dark:text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <Save size={14} /> Permanencia en Tablero
                </label>
                <div className="flex items-center gap-4 bg-slate-50 dark:bg-white/5 p-4 rounded-2xl border border-slate-100 dark:border-white/5">
                  <input
                    type="number"
                    value={strategy.archive_threshold_days}
                    onChange={(e) => setStrategy({ ...strategy, archive_threshold_days: parseInt(e.target.value) })}
                    className="w-20 bg-transparent text-3xl font-black text-slate-900 dark:text-white focus:outline-none border-r border-slate-200 dark:border-white/10"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-900 dark:text-white">Días</p>
                    <p className="text-[10px] text-slate-400">Días que un lead Ganado/Perdido sigue visible en Kanban</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          {message.text && (
            <div className={`p-6 rounded-[2rem] text-center text-sm font-bold animate-in zoom-in duration-300 shadow-lg border ${message.type === 'success'
                ? 'bg-green-500 text-white border-green-600'
                : 'bg-red-500 text-white border-red-600'
              }`}>
              {message.text}
            </div>
          )}
        </div>
      </div>

      {/* AI Usage Dashboard */}
      <AiUsageChart />

      {/* Fullscreen Prompt Editor Modal */}
      <PromptEditorModal
        isOpen={activeModalPrompt.isOpen}
        onClose={() => setActiveModalPrompt((prev) => ({ ...prev, isOpen: false }))}
        title={activeModalPrompt.title}
        promptType={activeModalPrompt.type}
        value={activeModalPrompt.type === 'master' ? (strategy.master_prompt || '') : (strategy.media_prompt || '')}
        onChange={(val) => {
          if (activeModalPrompt.type === 'master') {
            setStrategy((prev) => ({ ...prev, master_prompt: val }));
          } else {
            setStrategy((prev) => ({ ...prev, media_prompt: val }));
          }
        }}
        onSave={async (val) => {
          const field = activeModalPrompt.type === 'master' ? 'master_prompt' : 'media_prompt';
          const updatedStrategy = {
            ...strategy,
            [field]: val
          };
          setStrategy(updatedStrategy);
          setSaving(true);
          setMessage({ type: '', text: '' });
          try {
            await axios.post('/api/leads/settings', updatedStrategy);
            setMessage({ type: 'success', text: 'Prompt guardado correctamente' });
            fetchSettings();
          } catch (error) {
            setMessage({ type: 'error', text: 'Error al guardar el prompt' });
          } finally {
            setSaving(false);
          }
        }}
        saving={saving}
      />
    </div>
  );
};

export default AiStrategySettings;
